// components/sections/DisputeResolutionSection.js
import React from 'react';
import InteractiveField from '../common/InteractiveField';

/**
 * Компонент для раздела "7. Порядок разрешения споров".
 * @param {object} data - Объект состояния для этого раздела.
 * @param {function} onChange - Функция для обновления состояния.
 */
const DisputeResolutionSection = ({ data, onChange }) => {
  // Безопасная деструктуризация с фолбэками
  const {
    jurisdiction = { enabled: false, courtName: '', courtAddress: '' },
    claimProcedure = { enabled: true, claimPeriodDays: 10 },
    claimSendingMethods = { byPost: true, byEmail: false, byMessenger: false }
  } = data;

  // ✨ ИЗМЕНЕНИЕ: Добавлена логика для авто-выбора способа отправки
  const handleNestedChange = (section, field, value) => {
    const newData = { ...data, [section]: { ...data[section], [field]: value } };
    
    // Если включаем "Претензионный порядок"
    if (section === 'claimProcedure' && field === 'enabled' && value === true) {
      const hasEnabledMethod = Object.values(newData.claimSendingMethods).some(Boolean);
      // И если ни один способ не выбран, включаем "По адресу" по умолчанию
      if (!hasEnabledMethod) {
        newData.claimSendingMethods.byPost = true;
      }
    }
    onChange(newData);
  };

  // ✨ ИЗМЕНЕНИЕ: Добавлена логика для запрета отключения последнего способа отправки
  const handleSendingMethodChange = (method) => {
    const currentMethods = claimSendingMethods;
    const newMethods = {
      ...currentMethods,
      [method]: !currentMethods[method]
    };

    // Проверяем, остался ли хоть один выбранный способ
    const hasEnabledMethod = Object.values(newMethods).some(Boolean);

    if (!hasEnabledMethod) {
      // Если ничего не выбрано, выключаем весь претензионный порядок
      onChange({
        ...data,
        claimProcedure: {
          ...claimProcedure,
          enabled: false
        },
        claimSendingMethods: newMethods
      });
    } else {
      onChange({
        ...data,
        claimSendingMethods: newMethods
      });
    }
  };

  // Счетчик для динамической нумерации
  let clauseCounter = 1;

  // Формируем список активных способов отправки претензии
  const activeSendingMethods = [
    claimSendingMethods.byPost && (
      <li key="post" className="list-item">
        - ценным письмом с описью и уведомлением о вручении по месту нахождения;
      </li>
    ),
    claimSendingMethods.byEmail && (
      <li key="email" className="list-item">
        - по электронной почте, указанной в настоящем Договоре;
      </li>
    ),
    claimSendingMethods.byMessenger && (
      <li key="messenger" className="list-item">
        - через социальные сети и мессенджеры, через которые ранее осуществлялась переписка.
      </li>
    )
  ].filter(Boolean);

  return (
    <div className="document-preview-editor">
      <h2 className="special-section-title" style={{ width: '100%', display: 'block', textAlign: 'center', marginBottom: '24px' }}>
        7. Порядок разрешения споров
      </h2>

      {/* --- Пункт 7.1 (Базовый) --- */}
      <p className="document-preamble">
        <b>7.{clauseCounter}.</b> Споры, которые не могут быть урегулированы Сторонами путем переговоров, передаются на рассмотрение в суд в порядке, установленном настоящим Договором и действующим законодательством Российской Федерации.
      </p>

      {/* --- Конфигурируемый блок: Подсудность и Претензионный порядок --- */}
      <div className="configurable-section dynamic-content-block active-green">
        {/* Jurisdiction Toggle */}
        <div className="option-pills-group" style={{ marginBottom: '12px', justifyContent: 'flex-start' }}>
          <button
            className={`option-pill-toggle ${jurisdiction.enabled ? 'selected' : ''}`}
            onClick={() => handleNestedChange('jurisdiction', 'enabled', !jurisdiction.enabled)}
          >
            {jurisdiction.enabled ? '✓ Договорная подсудность' : '+ Договорная подсудность'}
          </button>
        </div>

        {jurisdiction.enabled && (
          <p className="document-preamble">
            <b>7.{++clauseCounter}.</b> Стороны пришли к соглашению, что споры вытекающие из настоящего Договора передаются на рассмотрение в{' '}
            <InteractiveField
              value={jurisdiction.courtName}
              onChange={(v) => handleNestedChange('jurisdiction', 'courtName', v)}
              placeholder="наименование суда"
              isRequired={true}
            />
            , адрес:{' '}
            <InteractiveField
              value={jurisdiction.courtAddress}
              onChange={(v) => handleNestedChange('jurisdiction', 'courtAddress', v)}
              placeholder="адрес суда"
              isRequired={true}
            />.
          </p>
        )}

        {/* Claim Procedure Toggle */}
        <div className="option-pills-group" style={{ marginBottom: '12px', marginTop: jurisdiction.enabled ? '16px' : '0', justifyContent: 'flex-start' }}>
          <button
            className={`option-pill-toggle ${claimProcedure.enabled ? 'selected' : ''}`}
            onClick={() => handleNestedChange('claimProcedure', 'enabled', !claimProcedure.enabled)}
          >
            {claimProcedure.enabled ? '✓ Претензионный порядок' : '+ Претензионный порядок'}
          </button>
        </div>

        {claimProcedure.enabled && (
          <p className="document-preamble">
            <b>7.{++clauseCounter}.</b> До обращения в суд для сторон обязателен досудебный (претензионный) порядок урегулирования споров. Сторона, получившая претензию, должна рассмотреть ее и направить ответ в{' '}
            <InteractiveField
              value={claimProcedure.claimPeriodDays}
              onChange={(v) => handleNestedChange('claimProcedure', 'claimPeriodDays', v)}
              placeholder="10"
              type="number"
              isRequired={true}
            />
            -дневный срок с даты получения. По истечении срока ответа на претензию направившая сторона вправе передать спор на рассмотрение в суд.
          </p>
        )}
      </div>

      {/* --- Блок, зависящий от Претензионного порядка --- */}
      {claimProcedure.enabled && (
        <>
          {/* --- Пункт 7.4 (Способ направления) --- */}
          <div className="configurable-section dynamic-content-block active-green">
            <p className="document-preamble">
                <b>7.{++clauseCounter}.</b> Претензия направляется любым из следующих способов:
            </p>
            
            <div className="indented-list-block" style={{ listStyleType: 'none', paddingLeft: '24px', marginTop: '0' }}>
                {/* By Post */}
                <div style={{ marginBottom: '12px' }}>
                    <button
                      className={`option-pill-toggle ${claimSendingMethods.byPost ? 'selected' : ''}`}
                      onClick={() => handleSendingMethodChange('byPost')}
                      style={{ marginBottom: '8px' }}
                    >
                      {claimSendingMethods.byPost ? '✓ По адресу нахождения' : '+ По адресу нахождения'}
                    </button>
                    {claimSendingMethods.byPost && (
                        <li className="list-item">
                            - ценным письмом с описью и уведомлением о вручении по месту нахождения;
                        </li>
                    )}
                </div>

                {/* By Email */}
                <div style={{ marginBottom: '12px' }}>
                    <button
                      className={`option-pill-toggle ${claimSendingMethods.byEmail ? 'selected' : ''}`}
                      onClick={() => handleSendingMethodChange('byEmail')}
                      style={{ marginBottom: '8px' }}
                    >
                      {claimSendingMethods.byEmail ? '✓ По электронной почте' : '+ По электронной почте'}
                    </button>
                    {claimSendingMethods.byEmail && (
                        <li className="list-item">
                            - по электронной почте, указанной в настоящем Договоре;
                        </li>
                    )}
                </div>

                {/* By Messenger */}
                <div style={{ marginBottom: '12px' }}>
                    <button
                      className={`option-pill-toggle ${claimSendingMethods.byMessenger ? 'selected' : ''}`}
                      onClick={() => handleSendingMethodChange('byMessenger')}
                      style={{ marginBottom: '8px' }}
                    >
                      {claimSendingMethods.byMessenger ? '✓ Через мессенджеры' : '+ Через мессенджеры'}
                    </button>
                    {claimSendingMethods.byMessenger && (
                        <li className="list-item">
                            - через социальные сети и мессенджеры, через которые ранее осуществлялась переписка.
                        </li>
                    )}
                </div>
            </div>
          </div>

          {/* --- Пункт 7.5 (Содержание претензии) --- */}
          <p className="document-preamble">
            <b>7.{++clauseCounter}.</b> В претензии необходимо описать требования и основания с указанием нарушенных условий настоящего Договора или норм законодательства. Вместе с претензией направляются копии документов, подтверждающие указанные в ней обстоятельства.
          </p>
        </>
      )}
    </div>
  );
};

export default DisputeResolutionSection;